import {Msisdn, normalizePhoneNumbers, PhoneNumberApiItem, RecordingMode} from "../Msisdns/msisdnService";
import {unwrapData} from "../../apiResponse.ts";
import {api} from "../../api.ts";
import {fetchAllPages} from "../../fetchAllPages.ts";


interface MsisdnSettingsInfo {
    msisdn: Msisdn;
    users: any[];
    groups: any[];
    emailsSetting: {
        enabled: boolean;
        email: string | null;
    };
    callRecordingEnabled: boolean;
    recordingMode?: RecordingMode;
    assigmentScope?: string;
}

export enum CallRulesStateEnum {
    AFTER_HOURS_MESSAGE = 'AFTER_HOURS_MESSAGE',
    WORKING_HOURS_WELCOME_MESSAGE = 'WORKING_HOURS_WELCOME_MESSAGE',
    VOICEMAIL_GREETING = 'VOICEMAIL_GREETING',
}

export interface WorkingHoursInterface {
    type: string;
    enabled: boolean;
    timeZone: any;
    workingHours: Array<{
        day: string;
        from: string;
        to: string;
        enabled: boolean;
    }>;
}
export interface CallRulesDTO {
    msisdnId?: string;
    workingForwardEnabled: boolean;
    workingForwardMsisdn: number | null;
    workingWelcomeMessageEnabled: boolean;
    nonWorkingForwardEnabled: boolean;
    nonWorkingForwardMsisdn: number | null;
    nonWorkingDontAcceptCalls: boolean;
    nonWorkingAfterHoursMessageEnabled: boolean;
    nonWorkingAfterHoursMessageOriginalName: string | null | undefined;
    nonWorkingAfterHoursMessageBucketName: string | null | undefined
    nonWorkingWelcomeMessageBucketName: string | null | undefined,
    workingWelcomeMessageOriginalName: string | null | undefined,
    workingWelcomeMessageBucketName: string | null | undefined,
    queueEnabled: boolean;
    queueId: number | null;
    voicemailEnabled: boolean;
    voicemailGreetingOriginalName?: string | null | undefined;
    voicemailGreetingBucketName?: string | null | undefined;
}
export interface CallRules {
    msisdnId?: string;
    workingWelcomeMessageUrl?: string;
    workingWelcomeMessageEnabled: boolean;
    workingForwardEnabled?: boolean;
    workingForwardMsisdn?: number | null;
    nonWorkingWelcomeMessageUrl?: string;
    nonWorkingDontAcceptCalls: boolean;
    nonWorkingForwardEnabled?: boolean;
    nonWorkingForwardMsisdn?: number | null;
    nonWorkingAfterHoursMessageEnabled: boolean;
    nonWorkingAfterHoursMessageUrl?: string;
    nonWorkingAfterHoursMessageOriginalName: string | null | undefined;
    nonWorkingAfterHoursMessageBucketName: string | null | undefined;
    nonWorkingWelcomeMessageBucketName: string | null | undefined;
    workingWelcomeMessageOriginalName: string | null | undefined;
    workingWelcomeMessageBucketName: string | null | undefined;
    queueEnabled: boolean;
    queueId: number | null;
    voicemailEnabled: boolean;
    voicemailGreetingOriginalName?: string | null | undefined;
    voicemailGreetingBucketName?: string | null | undefined;
    voicemailGreetingUrl?: string;
    /** Set (e.g. DESTINATION_NOT_AUTHORIZED) when a stored forward is refused by call restrictions. Computed on read. */
    forwardingPausedReason?: string | null;
}

export interface AudioFilesURL {
    workingWelcomeMessageUrl: string | undefined;
    nonWorkingWelcomeMessageUrl: string | undefined;
    voicemailGreetingUrl: string | undefined;
}


export const msisdnSettingsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        fetchMsisdnsList: builder.query<{ data: Msisdn[]; totalCount: number }, { phrase?: string | null, userId: string | null }>({
            queryFn: async ({phrase, userId}, _queryApi, _extraOptions, baseQuery) => {
                const result = await fetchAllPages<PhoneNumberApiItem>(
                    baseQuery,
                    ({limit, offset}) => ({
                        url: `/api/v1/phone-numbers`,
                        params: {userId, phrase, limit, offset},
                    }),
                    (data) => unwrapData(data as { data: PhoneNumberApiItem[] }) ?? [],
                );
                if ('error' in result) {
                    return {error: result.error};
                }
                return {data: {data: normalizePhoneNumbers(result.items), totalCount: result.totalCount}};
            },
            providesTags: ['MsisdnSettingsInfo'],
        }),
        fetchMsisdnSettingsInfo: builder.query<MsisdnSettingsInfo, string>({
            query: (id) => `/api/v1/phone-numbers/${id}/settings/info`,
            transformResponse: (response: { data: MsisdnSettingsInfo }) => unwrapData(response),
            providesTags: (id) => [{type: 'MsisdnSettingsInfo', msisdnId: id}],
        }),

        fetchMsisdnWorkingHours: builder.query<WorkingHoursInterface, string>({
            query: (id) => `/api/v1/phone-numbers/${id}/settings/working-hours`,
            transformResponse: (response: { data: WorkingHoursInterface }) => unwrapData(response),
            providesTags: (id) => [{type: 'MsisdnWorkingHours', msisdnId: id}],
        }),

        fetchCallRules: builder.query<CallRules, string>({
            query: (id) => `/api/v1/phone-numbers/${id}/settings/call-rules`,
            transformResponse: (response: { data: CallRules }) => unwrapData(response),
            providesTags: (id) => [{type: 'MsisdnCallRules', msisdnId: id}],
        }),

        updateWorkingHours: builder.mutation<void, { id: string; data: WorkingHoursInterface }>({
            query: ({id, data}) => ({
                url: `/api/v1/phone-numbers/${id}/settings/working-hours`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_, __, {id}) => [{type: 'MsisdnWorkingHours', id}],
            async onQueryStarted({id, data}, {dispatch, queryFulfilled}) {
                let patchResult; // Declare outside
                try {
                    patchResult = dispatch(
                        msisdnSettingsApi.util.updateQueryData('fetchMsisdnWorkingHours', id, (draft) => {
                            Object.assign(draft, data);
                        })
                    );

                    await queryFulfilled;
                } catch {
                    if (patchResult) {
                        patchResult.undo();
                    }
                }
            },
        }),

        updateCallRules: builder.mutation<CallRules, { id: string; data: CallRules }>({
            query: ({id, data}) => ({
                url: `/api/v1/phone-numbers/${id}/settings/call-rules`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_, __, {id}) => [{type: 'MsisdnCallRules', id}, 'Queue'],
            async onQueryStarted({id, data}, {dispatch, queryFulfilled}) {
                let patchResult; // Declare outside
                try {
                    patchResult = dispatch(
                        msisdnSettingsApi.util.updateQueryData('fetchCallRules', id, (draft) => {
                            Object.assign(draft, data);
                        })
                    );

                    await queryFulfilled;
                } catch {
                    if (patchResult) {
                        patchResult.undo();
                    }
                }
            },
        }),

        updateMsisdnSettingsInfo: builder.mutation<MsisdnSettingsInfo, { id: string; body: MsisdnSettingsInfo }>(
            {
                query: ({id, body}) => ({
                    url: `/api/v1/phone-numbers/${id}/settings/info`,
                    method: 'PUT',
                    body: body,
                }),
                transformResponse: (response: { data: MsisdnSettingsInfo }) => unwrapData(response),
                invalidatesTags: (_, __, {id}) => [
                    {type: 'MsisdnSettingsInfo', id},
                    {type: 'MsisdnSettingsInfo'},
                    {type: 'Profile'},
                    {type: 'Numbers'}
                ],
            }),

        postRecordingPreview: builder.mutation<any, { id: string; data: FormData; audioURL: string }>({
            query: ({id, data}) => ({
                url: `/api/v1/phone-numbers/${id}/settings/call-rules/message`,
                method: 'PUT',
                body: data,
            }),
            transformResponse: (response: any, _meta: any, arg: { id: string; data: FormData; audioURL: string }) => {
                const file = unwrapData(response);
                return {
                    ...file,
                    audioURL: arg.audioURL,
                };
            },
        }),


        getRecording: builder.query<{ data: string; type: string }, { id: string; type: string }>({
            query: ({ id, type }) => ({
                url: `/api/v1/phone-numbers/${id}/settings/call-rules/message`,
                params: { type },
                responseHandler: async (response: any) => {
                    const arrayBuffer = await response.arrayBuffer();
                    return {
                        buffer: arrayBuffer,
                        headers: response.headers,
                    };
                },
            }),
            transformResponse: (response: { buffer: ArrayBuffer; headers: Headers }) => {
                const contentType = response.headers.get('content-type') || 'audio/mpeg';
                const blob = new Blob([response.buffer], { type: contentType });
                const audioUrl = URL.createObjectURL(blob);

                return {
                    data: audioUrl,
                    type: contentType,
                };
            },
            keepUnusedDataFor: 0,
        }),
        synthetizeText: builder.mutation<any, { text: string, gender: string, language: string }>({
            query: ({ text, gender, language }) => ({
                url: `/api/v1/tts-speech`,
                method: 'POST',
                body: { text, gender, language },
                responseHandler: async (response: any) => {
                    const arrayBuffer = await response.arrayBuffer();
                    return {
                        buffer: arrayBuffer,
                        headers: response.headers,
                    };
                },
            }),
            transformResponse: (response: { buffer: ArrayBuffer; headers: Headers }) => {
                const contentType = response.headers.get('content-type') || 'audio/mpeg';
                const blob = new Blob([response.buffer], {type: contentType});
                const audioUrl = URL.createObjectURL(blob);

                return {
                    data: audioUrl,
                    type: contentType,
                };
            },
        }),
        uploadSynthetizeText: builder.mutation<any, { id: string, text: string, gender: string, language: string }>({
            query: ({id, text, gender, language}) => ({
                url: `/api/v1/phone-numbers/${id}/settings/call-rules/tts-message`,
                method: 'PUT',
                body: {text, gender, language},
            }),
            transformResponse: (response: any) => unwrapData(response),
        }),
        fetchMsisdnsAdmin: builder.query<{ data: Msisdn[]; totalCount: number }, { phrase:string | null}>({
            query: ({phrase}) => ({
                url: `/api/admin/v1/phone-numbers`,
                params: { phrase },
            }),
            transformResponse: (response: { data: Msisdn[] }, meta) => {
                const totalCount = meta?.response?.headers.get('x-total-count');
                return {
                    data: unwrapData(response),
                    totalCount: totalCount ? parseInt(totalCount, 10) : 0
                };
            },
            providesTags: ['MsisdnSettingsInfo'],
        }),
    }),
});

export const {
    useFetchMsisdnsListQuery,
    useFetchMsisdnSettingsInfoQuery,
    useFetchMsisdnWorkingHoursQuery,
    useFetchCallRulesQuery,
    useUpdateWorkingHoursMutation,
    useUpdateCallRulesMutation,
    useUpdateMsisdnSettingsInfoMutation,
    usePostRecordingPreviewMutation,
    useSynthetizeTextMutation,
    useUploadSynthetizeTextMutation,
    useGetRecordingQuery,
    useFetchMsisdnsAdminQuery,
} = msisdnSettingsApi;
